import { useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import Slider from 'react-slick';
// UI
import Carousel from 'ui3/Carousel/Carousel';
import Icon from 'ui/Icon/Icon';
import Image from 'ui/Image/Image';
// Styles
import styles from './ProductImages.module.scss';

export type ProductImageType = {
  id: string;
  imageUrl: string | null;
  isMainImage: boolean;
};

type ProductImagesProps = {
  images: ProductImageType[];
  productName: string;
};

const orderImagesWithMainFirst = (
  images: ProductImageType[]
): ProductImageType[] => {
  if (!images.length) return images;
  const main = images.filter((x) => x.isMainImage);
  const rest = images.filter((x) => !x.isMainImage);
  return [...main, ...rest];
};

const ProductImages = ({ images, productName }: ProductImagesProps) => {
  const [currentImage, setCurrentImage] = useState<ProductImageType | null>(
    null
  );
  const carouselRef = useRef<Slider | null>(null);

  const allImages = useMemo(() => orderImagesWithMainFirst(images), [images]);

  const initialImage = useMemo(() => {
    const main = allImages.find((img) => img.isMainImage);
    return main ?? allImages[0] ?? null;
  }, [allImages]);

  const initialSlideIndex = Math.max(
    0,
    allImages.findIndex((img) => img?.imageUrl === initialImage?.imageUrl)
  );

  useEffect(() => {
    setCurrentImage(initialImage);
    carouselRef.current?.slickGoTo?.(initialSlideIndex, true);
  }, [initialImage, initialSlideIndex]);

  const isThumbnailsVisible = allImages.length > 1;

  const currentImageIndex =
    allImages?.findIndex((item) => item?.imageUrl === currentImage?.imageUrl) ??
    0;

  const onClickLeft = () => {
    if (allImages?.length) {
      if (currentImageIndex === 0) {
        setCurrentImage(allImages[allImages.length - 1]);
      } else {
        setCurrentImage(allImages[currentImageIndex - 1]);
      }
    }
  };

  const onClickRight = () => {
    if (allImages?.length) {
      if (currentImageIndex === allImages.length - 1) {
        setCurrentImage(allImages[0]);
      } else {
        setCurrentImage(allImages[currentImageIndex + 1]);
      }
    }
  };

  const handleThumbnailClick = (image: ProductImageType) => {
    setCurrentImage(image);
  };

  const renderThumbnailsList = () => {
    if (!isThumbnailsVisible) return null;
    return (
      <ul className={styles.list}>
        {allImages.map((item) => {
          const imageFileUrl = item?.imageUrl || '';
          return (
            <li
              key={item.id}
              className={cn(styles.listItem, {
                [styles.active]: imageFileUrl === currentImage?.imageUrl,
              })}
            >
              <button
                className={styles.thumbnailButton}
                onClick={() => handleThumbnailClick(item)}
              >
                <Image
                  className={styles.image}
                  src={imageFileUrl}
                  alt={productName}
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center center"
                  loading="lazy"
                  sizes="(max-width: 540px) 100vw, 150px"
                />
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  if (!allImages.length) return null;

  return (
    <div className={styles.root}>
      <div className={styles.flexWrapper}>
        <div className={styles.imageWrapper}>
          <div className={styles.mobileCarouselWrapper}>
            <Carousel
              ref={carouselRef}
              infinite={allImages.length > 1}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              dots={false}
              swipeToSlide
              speed={300}
              initialSlide={initialSlideIndex}
              afterChange={(index) =>
                allImages[index] && setCurrentImage(allImages[index])
              }
              className={styles.productCarousel}
            >
              {allImages.map(
                (item) =>
                  item?.imageUrl && (
                    <div
                      key={item.id}
                      className={styles.mobileSlide}
                      role="listitem"
                    >
                      <Image
                        src={item.imageUrl}
                        alt={productName}
                        layout="fill"
                        objectFit="cover"
                        objectPosition="center center"
                        priority={item.imageUrl === initialImage?.imageUrl}
                        sizes="(max-width: 540px) 100vw, 540px"
                      />
                    </div>
                  )
              )}
            </Carousel>
          </div>
          <div className={styles.desktopImageBlock}>
            <Image
              src={currentImage?.imageUrl || ''}
              alt={productName}
              title={productName ? `${productName} on MILLIONS` : undefined}
              layout="fill"
              objectFit="cover"
              objectPosition="center center"
              priority
              sizes="(max-width: 540px) 100vw, 540px"
            />
            <div className={styles.arrowIconContainer}>
              <Icon
                name="chevron-left-new"
                className={cn(styles.arrowIcon, {
                  [styles.hidden]: !isThumbnailsVisible,
                })}
                onClick={onClickLeft}
              />
              <Icon
                name="chevron-right-new"
                className={cn(styles.arrowIcon, {
                  [styles.hidden]: !isThumbnailsVisible,
                })}
                onClick={onClickRight}
              />
            </div>
          </div>
          <div
            className={cn(styles.imageIndicatorWrapper, {
              [styles.hidden]: !isThumbnailsVisible,
            })}
          >
            {isThumbnailsVisible && (
              <div className={styles.imageIndicators}>
                {allImages.map((item, index) =>
                  item?.imageUrl ? (
                    <div
                      key={item.id}
                      className={cn(styles.imageIndicator, {
                        [styles.currentImageIndicator]:
                          currentImageIndex === index,
                      })}
                    />
                  ) : null
                )}
              </div>
            )}
          </div>
        </div>
        {renderThumbnailsList()}
      </div>
    </div>
  );
};

export default ProductImages;
